import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '@mdi/react';
import {
    mdiSchool, mdiPlus, mdiPencil, mdiTrashCan,
} from '@mdi/js';
import { toast } from 'react-toastify';
import AppHeader from '../../components/AppHeader';
import { classApi } from '../../api/classes';
import ConfirmationModal from '../../components/ConfirmationModal';
import '../Common.css';
import '../Dashboard.css';
import '../header.css';
import './ClassTermsPage.css';
import './ClassDefinitionsPage.css';
const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const LEVEL_OPTIONS = ['Beginner', 'Intermediate', 'Advanced', 'All Levels'];

const ClassDefinitionsPage = () => {
    const navigate = useNavigate();
    const { termId } = useParams();

    const [term, setTerm] = useState(null);
    const [classes, setClasses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingClass, setEditingClass] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, onConfirm }

    const [formName, setFormName] = useState('');
    const [formStyle, setFormStyle] = useState('');
    const [formLevel, setFormLevel] = useState('Beginner');
    const [formDay, setFormDay] = useState('Monday');
    const [formStartTime, setFormStartTime] = useState('19:00');
    const [formEndTime, setFormEndTime] = useState('20:00');
    const [formLocation, setFormLocation] = useState('');
    const [formMaxStudents, setFormMaxStudents] = useState('');
    const [formDescription, setFormDescription] = useState('');

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [termsRes, classesRes] = await Promise.all([
                classApi.getTerms(),
                classApi.getClasses(termId),
            ]);
            if (termsRes.ok) {
                const found = (termsRes.data.terms || []).find(t => t.id === termId);
                setTerm(found || null);
            }
            if (classesRes.ok) {
                setClasses(classesRes.data.classes || []);
            } else {
                toast.error(classesRes.data?.message || 'Failed to load classes');
            }
        } catch {
            toast.error('Failed to load classes');
        } finally {
            setIsLoading(false);
        }
    }, [termId]);

    useEffect(() => { loadData(); }, [loadData]);

    const openCreateForm = () => {
        setEditingClass(null);
        setFormName('');
        setFormStyle('');
        setFormLevel('Beginner');
        setFormDay('Monday');
        setFormStartTime('19:00');
        setFormEndTime('20:00');
        setFormLocation('');
        setFormMaxStudents('');
        setFormDescription('');
        setShowForm(true);
    };

    const openEditForm = (cls) => {
        setEditingClass(cls);
        setFormName(cls.name || '');
        setFormStyle(cls.style || '');
        setFormLevel(cls.level || 'Beginner');
        setFormDay(cls.dayOfWeek || 'Monday');
        setFormStartTime(cls.startTime || '19:00');
        setFormEndTime(cls.endTime || '20:00');
        setFormLocation(cls.location || '');
        setFormMaxStudents(cls.maxStudents ? String(cls.maxStudents) : '');
        setFormDescription(cls.description || '');
        setShowForm(true);
    };

    const addOneHour = (timeStr) => {
        const [h, m] = timeStr.split(':').map(Number);
        const total = h * 60 + m + 60;
        const hh = String(Math.floor(total / 60) % 24).padStart(2, '0');
        const mm = String(total % 60).padStart(2, '0');
        return `${hh}:${mm}`;
    };

    const handleSave = async () => {
        if (!formName || !formDay || !formStartTime || !formEndTime) {
            toast.error('Name, day, start time and end time are required');
            return;
        }
        if (formEndTime <= formStartTime) {
            toast.error('End time must be after start time');
            return;
        }
        setIsSaving(true);
        try {
            const data = {
                termId,
                name: formName,
                style: formStyle,
                level: formLevel,
                dayOfWeek: formDay,
                startTime: formStartTime,
                endTime: formEndTime,
                location: formLocation,
                maxStudents: formMaxStudents ? parseInt(formMaxStudents, 10) : undefined,
                description: formDescription,
            };
            const response = editingClass
                ? await classApi.updateClass(editingClass.id, data)
                : await classApi.createClass(data);

            if (response.ok) {
                toast.success(editingClass ? 'Class updated' : 'Class created');
                setShowForm(false);
                loadData();
            } else {
                toast.error(response.data?.message || 'Failed to save class');
            }
        } catch {
            toast.error('Failed to save class');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = (cls) => {
        setConfirmModal({
            title: 'Delete Class',
            message: `Delete "${cls.name}"? This cannot be undone.`,
            variant: 'danger',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const response = await classApi.deleteClass(cls.id);
                    if (response.ok) {
                        toast.success('Class deleted');
                        loadData();
                    } else {
                        toast.error(response.data?.message || 'Cannot delete class');
                    }
                } catch {
                    toast.error('Failed to delete class');
                }
            },
        });
    };

    return (
        <div className="dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button onClick={() => navigate('/classes/terms')} className="back-nav-btn">
                                ← Terms
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon path={mdiSchool} size={0.9} color="#451f78" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                {term ? term.name : 'Classes'}
                            </h1>
                        </div>
                    </div>

                    <div className="content-card class-terms-card class-def-card">
                        <div className="class-terms-actions-bar">
                            <h3>Classes {!isLoading && `(${classes.length})`}</h3>
                            <button className="class-terms-add-btn" onClick={openCreateForm}>
                                <Icon path={mdiPlus} size={0.9} /> New Class
                            </button>
                        </div>

                        {isLoading ? (
                            <p className="class-terms-loading">Loading classes...</p>
                        ) : classes.length === 0 ? (
                            <p className="class-terms-empty">No classes yet. Add the first class for this term.</p>
                        ) : (
                            <div className="class-terms-list">
                                {classes.map(cls => (
                                    <div key={cls.id} className="class-term-row">
                                        <div className="class-term-info">
                                            <div className="class-term-name">
                                                {cls.name}
                                                {cls.level && <span className="class-term-type-badge">{cls.level}</span>}
                                                {cls.style && <span className="class-def-style-badge">{cls.style}</span>}
                                            </div>
                                            <div className="class-term-dates">
                                                {cls.dayOfWeek} · {cls.startTime}–{cls.endTime}
                                                {cls.location ? ` · ${cls.location}` : ''}
                                                {cls.maxStudents ? ` · Max ${cls.maxStudents}` : ''}
                                            </div>
                                        </div>
                                        <div className="class-term-row-actions">
                                            <button className="class-term-action-btn" onClick={() => openEditForm(cls)} title="Edit">
                                                <Icon path={mdiPencil} size={0.85} />
                                            </button>
                                            <button className="class-term-action-btn danger" onClick={() => handleDelete(cls)} title="Delete">
                                                <Icon path={mdiTrashCan} size={0.85} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showForm && (
                <div className="modal-overlay" onClick={() => setShowForm(false)}>
                    <div className="class-terms-modal-content class-def-form" onClick={e => e.stopPropagation()}>
                        <div className="class-terms-modal-header">
                            <h2>
                                <Icon path={mdiSchool} size={0.9} color="#451f78" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                {editingClass ? 'Edit Class' : 'New Class'}
                            </h2>
                            <button className="class-terms-modal-close" onClick={() => setShowForm(false)}>×</button>
                        </div>

                        <div className="class-terms-modal-body">
                            <label className="class-term-form-label">Class Name *</label>
                            <input className="class-term-form-input" value={formName}
                                onChange={e => setFormName(e.target.value)} placeholder="e.g. Salsa Beginner" />

                            <div className="class-def-form-row">
                                <div>
                                    <label className="class-term-form-label">Style</label>
                                    <input className="class-term-form-input" value={formStyle}
                                        onChange={e => setFormStyle(e.target.value)} placeholder="e.g. Salsa, Bachata" />
                                </div>
                                <div>
                                    <label className="class-term-form-label">Level</label>
                                    <select className="class-term-form-input" value={formLevel}
                                        onChange={e => setFormLevel(e.target.value)}>
                                        {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="class-def-form-row">
                                <div>
                                    <label className="class-term-form-label">Day *</label>
                                    <select className="class-term-form-input" value={formDay}
                                        onChange={e => setFormDay(e.target.value)}>
                                        {DAY_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="class-term-form-label">Start Time *</label>
                                    <input type="time" className="class-term-form-input" value={formStartTime}
                                        onChange={e => {
                                            const newStart = e.target.value;
                                            setFormStartTime(newStart);
                                            if (newStart >= formEndTime) {
                                                setFormEndTime(addOneHour(newStart));
                                            }
                                        }} />
                                </div>
                                <div>
                                    <label className="class-term-form-label">End Time *</label>
                                    <input type="time" className="class-term-form-input" value={formEndTime}
                                        onChange={e => setFormEndTime(e.target.value)} />
                                </div>
                            </div>

                            <label className="class-term-form-label">Location</label>
                            <input className="class-term-form-input" value={formLocation}
                                onChange={e => setFormLocation(e.target.value)} placeholder="e.g. Studio A" />

                            <label className="class-term-form-label">Max Students</label>
                            <input type="number" className="class-term-form-input" value={formMaxStudents}
                                onChange={e => setFormMaxStudents(e.target.value)} placeholder="Leave blank for unlimited" min="1" />

                            <label className="class-term-form-label">Description</label>
                            <textarea className="class-term-form-input class-def-textarea" value={formDescription}
                                onChange={e => setFormDescription(e.target.value)}
                                placeholder="Optional class description" rows={3} />
                        </div>

                        <div className="class-terms-modal-footer">
                            <button className="class-term-form-cancel" onClick={() => setShowForm(false)}>Cancel</button>
                            <button className="class-term-form-save" onClick={handleSave} disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Class'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmModal && (
                <ConfirmationModal
                    isOpen
                    title={confirmModal.title}
                    message={confirmModal.message}
                    variant={confirmModal.variant}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

export default ClassDefinitionsPage;
